import {
  ComplaintDto,
  ComplaintEscalationDto,
  ComplaintResolutionDto,
  ComplaintUserDto,
  ComplaintPhotoDto,
  ComplaintOrderItemDto,
  ComplaintEstimatedRefundDto,
  ComplaintEstimatedReleaseDto,
  ComplaintEstimatedReleaseDetailsDto,
  ComplaintEstimatedRefundDetailsDto,
  ComplaintEstimateRefundBeforeConversionDto,
  ComplaintRefundOfferDto,
  ComplaintModel,
  ComplaintEscalationModel,
  ComplaintResolutionModel,
  ComplaintUserModel,
  ComplaintPhotoModel,
  ComplaintOrderItemModel,
  ComplaintEstimatedRefundModel,
  ComplaintEstimatedReleaseModel,
  ComplaintEstimateReleaseDetailsModel,
  ComplaintEstimatedRefundDetailsModel,
  ComplaintEstimatedRefundBeforeConversionModel,
  ComplaintFaqEntryDto,
  ComplaintFaqEntryModel,
} from '../types/complaint'
import { transformCurrencyAmountDto } from './currency-amount'

export const transformComplaintPhoto = (photo: ComplaintPhotoDto): ComplaintPhotoModel => ({
  url: photo.url,
  highResolution: photo.high_resolution,
})

export const transformComplaintPhotos = (
  photoDtos: Array<ComplaintPhotoDto>,
): Array<ComplaintPhotoModel> => photoDtos.map(transformComplaintPhoto)

export const transformComplaintUser = (user: ComplaintUserDto): ComplaintUserModel => ({
  id: user.id,
  login: user.login,
})

export const transformComplaintResolution = (
  resolution: ComplaintResolutionDto,
): ComplaintResolutionModel => ({
  action: resolution.action,
  isPrimary: resolution.is_primary,
  tip: resolution.tip,
  button: resolution.button,
  confirmTitle: resolution.confirm_title,
  confirmButton: resolution.confirm_button,
  confirmation: resolution.confirmation,
  resolutions: resolution?.resolutions,
})

export const transformComplaintEscalation = (
  escalation: ComplaintEscalationDto,
): ComplaintEscalationModel => ({
  tip: escalation.tip,
  button: escalation.button,
  confirmTitle: escalation.confirm_title,
  confirmButton: escalation.confirm_button,
  confirmation: escalation.confirmation,
  englishAllowedVisible: escalation.english_allowed_visible,
  close: escalation.close,
})

export const transformComplaintOrderItem = (
  complaintOrderItem: ComplaintOrderItemDto,
): ComplaintOrderItemModel => ({
  orderItemId: complaintOrderItem.order_item_id,
  returnable: complaintOrderItem.returnable,
  reason: complaintOrderItem.reason,
  reasonText: complaintOrderItem.reason_text,
  item: {
    id: complaintOrderItem.item.id,
    title: complaintOrderItem.item.title,
    thumbnail: complaintOrderItem.item.thumbnail_150x210,
  },
})

export const transformEstimatedRefundDetailsDto = (
  estimatedRefundDetails: ComplaintEstimatedRefundDetailsDto,
): ComplaintEstimatedRefundDetailsModel => ({
  items: estimatedRefundDetails.items
    ? estimatedRefundDetails.items.map(({ title, original }) => ({
        title,
        original: transformCurrencyAmountDto(original),
      }))
    : null,
  itemsEstimatedRefundTotal: transformCurrencyAmountDto(
    estimatedRefundDetails.items_estimated_refund_total,
  ),
  totalItemsDiscount: estimatedRefundDetails.total_items_discount
    ? transformCurrencyAmountDto(estimatedRefundDetails.total_items_discount)
    : null,
  buyerProtectionFee: transformCurrencyAmountDto(estimatedRefundDetails.buyer_protection_fee),
  shipment: transformCurrencyAmountDto(estimatedRefundDetails.shipment),
  returnShipment: estimatedRefundDetails.return_shipment
    ? transformCurrencyAmountDto(estimatedRefundDetails.return_shipment)
    : null,
  authenticityCheckFee: estimatedRefundDetails.authenticity_check_fee
    ? transformCurrencyAmountDto(estimatedRefundDetails.authenticity_check_fee)
    : null,
  electronicsVerificationFee: estimatedRefundDetails.electronics_verification_fee
    ? transformCurrencyAmountDto(estimatedRefundDetails.electronics_verification_fee)
    : null,
  refundOffer: estimatedRefundDetails.refund_offer
    ? transformCurrencyAmountDto(estimatedRefundDetails.refund_offer)
    : null,
  salesTax: estimatedRefundDetails.sales_tax
    ? transformCurrencyAmountDto(estimatedRefundDetails.sales_tax)
    : null,
})

export const transformBeforeConversionDto = (
  beforeConversion: ComplaintEstimateRefundBeforeConversionDto,
): ComplaintEstimatedRefundBeforeConversionModel => ({
  details: {
    totalItemPrice: transformCurrencyAmountDto(beforeConversion.details.total_item_price),
    shipment: transformCurrencyAmountDto(beforeConversion.details.shipment),
    refundOffer: beforeConversion.details.refund_offer
      ? transformCurrencyAmountDto(beforeConversion.details.refund_offer)
      : null,
  },
  exchange: {
    baseRate: transformCurrencyAmountDto(beforeConversion.exchange.base_rate),
    exchangeRate: transformCurrencyAmountDto(beforeConversion.exchange.exchange_rate),
  },
  markup: beforeConversion.markup,
})

export const transformEstimatedRefund = (
  esimatedRefund: ComplaintEstimatedRefundDto,
): ComplaintEstimatedRefundModel => ({
  estimatedRefundWithoutShipment: esimatedRefund.estimated_refund_without_shipment
    ? transformCurrencyAmountDto(esimatedRefund.estimated_refund_without_shipment)
    : null,
  totalEstimatedRefund: transformCurrencyAmountDto(esimatedRefund.total_estimated_refund),
  details: transformEstimatedRefundDetailsDto(esimatedRefund.details),
  beforeConversion: esimatedRefund?.before_conversion
    ? transformBeforeConversionDto(esimatedRefund.before_conversion)
    : null,
})

export const transformEstimatedReleaseDetailsDto = (
  detailsDto: ComplaintEstimatedReleaseDetailsDto,
): ComplaintEstimateReleaseDetailsModel => ({
  totalItemsDiscount: detailsDto.total_items_discount
    ? transformCurrencyAmountDto(detailsDto.total_items_discount)
    : null,
  refundableShippingVat: detailsDto.refundable_shipping_vat
    ? transformCurrencyAmountDto(detailsDto.refundable_shipping_vat)
    : null,
  items: detailsDto.items
    ? detailsDto.items.map(item => ({
        title: item.title,
        original: transformCurrencyAmountDto(item.original),
      }))
    : null,
  refundOffer: detailsDto.refund_offer ? transformCurrencyAmountDto(detailsDto.refund_offer) : null,
})

export const transformEstimatedRelease = (
  estimatedRelease: ComplaintEstimatedReleaseDto,
): ComplaintEstimatedReleaseModel => ({
  totalPrice: transformCurrencyAmountDto(estimatedRelease.total_price),
  totalEstimatedRefund: transformCurrencyAmountDto(estimatedRelease.total_estimated_refund),
  totalEstimatedRelease: transformCurrencyAmountDto(estimatedRelease.total_estimated_release),
  details: transformEstimatedReleaseDetailsDto(estimatedRelease.details),
})

export const transformComplaintRefundOfferDto = (refundOffer: ComplaintRefundOfferDto) => ({
  id: refundOffer.id,
  isAccepted: refundOffer.accepted,
})

export const transformComplaintFaqEntryDto = (
  faqEntryDto: ComplaintFaqEntryDto,
): ComplaintFaqEntryModel => {
  const { id, title, body } = faqEntryDto

  return {
    id,
    body,
    title: title || '',
  }
}

export const transformComplaint = (complaint: ComplaintDto): ComplaintModel => ({
  buyerId: complaint.buyer_id,
  eligibleForReturnLabel: complaint.eligible_for_return_label,
  escalated: complaint.escalated,
  escalation: complaint.escalation ? transformComplaintEscalation(complaint.escalation) : null,
  id: complaint.id,
  message: complaint.message,
  transactionId: complaint.transaction_id,
  sellerId: complaint.seller_id,
  photos: transformComplaintPhotos(complaint.photos),
  resolutions: complaint.resolutions
    ? complaint.resolutions.map(transformComplaintResolution)
    : null,
  faqEntry: transformComplaintFaqEntryDto(complaint.faq_entry),
  user: transformComplaintUser(complaint.user),
  returnable: complaint.returnable ? complaint.returnable : null,
  complaintOrderItems: complaint.complaint_order_items
    ? complaint.complaint_order_items.map(transformComplaintOrderItem)
    : null,
  estimatedRefund: complaint.estimated_refund
    ? transformEstimatedRefund(complaint.estimated_refund)
    : null,
  estimatedRelease: complaint.estimated_release
    ? transformEstimatedRelease(complaint.estimated_release)
    : null,
  userMsgThreadId: complaint.user_msg_thread_id,
  isBundleOrder: complaint.is_bundle_order,
  orderType: complaint.order_type,
  refundOffer: complaint?.refund_offer
    ? transformComplaintRefundOfferDto(complaint.refund_offer)
    : null,
  type: complaint.type,
  ...(complaint.show_all_items_return_banner
    ? { showAllItemsReturnBanner: complaint.show_all_items_return_banner }
    : {}),
})

export const transformComplaintDto = ({
  complaint,
}: {
  complaint: ComplaintDto | null
}): ComplaintModel | null => (complaint ? transformComplaint(complaint) : null)
